// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { SpreadItem } from './SpreadItem';
export class SpreadPlan {
    /// <summary>
    /// List of spread settings for each instrument
    /// </summary>
    constructor(dc, message) {
        this.itemsByType = new Map();
        this.itemsByInstrumentId = new Map();
        this.defaultItem = null;
        this._dataCache = null;
        this._dataCache = dc;
        this.InitBy(message);
    }
    InitBy(message) {
        this.Id = message.Id.toString();
        this.Name = message.Name;
        const copyByType = new Map();
        const copyByInstrumentId = new Map();
        for (const si of message.SpreadItems) {
            const spreadItem = SpreadPlan.CreateSpreadItem(si);
            if (!isNullOrUndefined(si.InstrumentId)) {
                copyByInstrumentId.set(si.InstrumentId, spreadItem);
            }
            else if (si.InstrumentTypeId !== -1) {
                copyByType.set(si.InstrumentTypeId, spreadItem);
            }
            else {
                this.defaultItem = spreadItem;
            }
        }
        this.itemsByInstrumentId = copyByInstrumentId;
        this.itemsByType = copyByType;
    }
    GetItem(instrument) {
        if (isNullOrUndefined(instrument)) {
            return null;
        }
        return this.GetSpreadItem(instrument.Id, instrument.TypeId);
    }
    GetSpreadItem(instrumentId, instrumentTypeId) {
        if (!isValidString(instrumentId) || !isValidNumber(instrumentTypeId) || isNullOrUndefined(this._dataCache)) {
            return null;
        }
        if (this.itemsByInstrumentId.has(instrumentId)) {
            return this.itemsByInstrumentId.get(instrumentId);
        }
        while (instrumentTypeId !== -1) {
            if (this.itemsByType.has(instrumentTypeId)) {
                return this.itemsByType.get(instrumentTypeId);
            }
            const instGroup = this._dataCache.getInstrumentTypeById(instrumentTypeId);
            instrumentTypeId = !isNullOrUndefined(instGroup) ? instGroup.SuperTypeId : -1;
        }
        if (!isNullOrUndefined(this.defaultItem)) {
            return this.defaultItem;
        }
        return null;
    }
    IsNotEmptyPlan(instrument) {
        const item = this.GetItem(instrument);
        if (item === null) {
            return false;
        }
        if (item.SpreadMode !== SpreadPlan.NOT_FIXED_SPREADMODE || item.BidShift !== 0 || item.AskShift !== 0) {
            return true;
        }
        else {
            return false;
        }
    }
    /// <summary>
    /// Расчёт цен
    /// </summary>
    CalcBid(bid, ask, ins, round = true) {
        // No spread rules for this instrument?
        const spreadItem = this.GetItem(ins);
        if (isNullOrUndefined(spreadItem)) {
            return bid;
        }
        return spreadItem.CalcBid(bid, ask, ins, round);
    }
    CalcAsk(bid, ask, ins, round = true) {
        // No spread rules for this instrument?
        const spreadItem = this.GetItem(ins);
        if (isNullOrUndefined(spreadItem)) {
            return ask;
        }
        return spreadItem.CalcAsk(bid, ask, ins, round);
    }
    static CreateSpreadItem(item) {
        const si = new SpreadItem();
        si.AskShift = item.AskShift;
        si.BidShift = item.BidShift;
        si.Spread = item.Spread;
        si.SpreadMode =
            item.SpreadMode === SpreadPlan.NOT_FIXED_SPREADMODE &&
                (item.SpreadMeasure === SpreadMeasureMode.SPREAD_IN_BASIC_POINTS ||
                    item.SpreadMeasure === SpreadMeasureMode.SPREAD_IN_BASIC_POINTS_POST_EXECUTION)
                ? SpreadPlan.DYNAMIC_SPREADMODE
                : item.SpreadMode;
        si.SpreadMeasure = item.SpreadMeasure;
        return si;
    }
    IsNeedLoadHistoryForSpread(instrument, historyType) {
        // дополнительные проверки, нужно ли закачивать историю
        const item = this.GetItem(instrument);
        if (isNullOrUndefined(item)) {
            return false;
        }
        if (item.SpreadMode === SpreadPlan.ASK_SPREADMODE && historyType === HistoryType.QUOTE_ASK) {
            return false;
        }
        if (item.SpreadMode === SpreadPlan.BID_SPREADMODE && historyType === HistoryType.QUOTE_LEVEL1) {
            return false;
        }
        if (item.SpreadMode === SpreadPlan.DYNAMIC_SPREADMODE) {
            return false;
        }
        if (item.SpreadMode === SpreadPlan.NOT_FIXED_SPREADMODE) {
            return false;
        }
        return true;
    }
}
SpreadPlan.NOT_FIXED_SPREADMODE = 0;
SpreadPlan.ASK_SPREADMODE = 1;
SpreadPlan.BID_SPREADMODE = 2;
SpreadPlan.BID_ASK_SPREADMODE = 3;
SpreadPlan.LIMEN_SPREADMODE = 4;
SpreadPlan.DYNAMIC_SPREADMODE = 1000; // вымышленный тип под динамический спред в базовых пунктах.
export var SpreadMeasureMode;
(function (SpreadMeasureMode) {
    SpreadMeasureMode[SpreadMeasureMode["SPREAD_IN_PIPS"] = 0] = "SPREAD_IN_PIPS";
    SpreadMeasureMode[SpreadMeasureMode["SPREAD_IN_CURRENCY"] = 1] = "SPREAD_IN_CURRENCY";
    SpreadMeasureMode[SpreadMeasureMode["SPREAD_IN_BASIC_POINTS"] = 2] = "SPREAD_IN_BASIC_POINTS";
    SpreadMeasureMode[SpreadMeasureMode["SPREAD_IN_BASIC_POINTS_POST_EXECUTION"] = 3] = "SPREAD_IN_BASIC_POINTS_POST_EXECUTION";
    SpreadMeasureMode[SpreadMeasureMode["SPREAD_IN_CURRENCY_POST_EXECUTION"] = 4] = "SPREAD_IN_CURRENCY_POST_EXECUTION";
})(SpreadMeasureMode || (SpreadMeasureMode = {}));
